import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiArrowLeft,
  mdiPhone,
  mdiEmail,
  mdiCarOutline,
  mdiCalendarOutline,
} from "@mdi/js";
import { Chip } from "@heroui/react";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import { ROUTES } from "@/constants/routes";

/** Скрол-порог в px: после него переключатель года прячется */
const SCROLL_HIDE_THRESHOLD = 300;

type YearNavPhase = "visible" | "exiting" | "hidden" | "entering";

function getInitials(firstName: string, lastName: string): string {
  const f = firstName?.trim()?.[0] ?? "";
  const l = lastName?.trim()?.[0] ?? "";
  return (f + l).toUpperCase();
}

const ClientHeader: React.FC = observer(() => {
  const navigate = useNavigate();
  const client = clientDetailStore.client;
  const { statsYear, setStatsYear } = clientDetailStore;
  const currentYear = new Date().getFullYear();
  const yearOptions = [statsYear - 1, statsYear, statsYear + 1];

  const [phase, setPhase] = useState<YearNavPhase>("visible");
  const phaseTimer = useRef<number>(0);

  useEffect(() => {
    if (!client) return;
    const container = document.querySelector(
      ".app-layout__content",
    ) as HTMLElement | null;
    if (!container) return;

    const onScroll = () => {
      const past = container.scrollTop > SCROLL_HIDE_THRESHOLD;
      setPhase((current) => {
        if (past && (current === "visible" || current === "entering")) {
          clearTimeout(phaseTimer.current);
          phaseTimer.current = window.setTimeout(() => setPhase("hidden"), 240);
          return "exiting";
        }
        if (!past && (current === "hidden" || current === "exiting")) {
          clearTimeout(phaseTimer.current);
          phaseTimer.current = window.setTimeout(
            () => setPhase("visible"),
            360,
          );
          return "entering";
        }
        return current;
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      clearTimeout(phaseTimer.current);
    };
  }, [client]);

  if (!client) return null;

  const yearNavClass =
    phase === "exiting"
      ? "cdp-year-nav cdp-year-nav--exiting"
      : phase === "hidden"
        ? "cdp-year-nav cdp-year-nav--gone"
        : phase === "entering"
          ? "cdp-year-nav cdp-year-nav--entering"
          : "cdp-year-nav";

  return (
    <div className="cdp-header" data-testid="cdp-header">
      <div className="cdp-header__info">
        <div className="cdp-header__top-row">
          <button
            className="cdp-back"
            onClick={() => navigate(ROUTES.CLIENTS)}
            aria-label="К списку клиентов"
            data-testid="cdp-back-button"
          >
            <Icon path={mdiArrowLeft} size={0.7} />
          </button>
          <div className="cdp-avatar">
            <span className="cdp-avatar__initials">
              {getInitials(client.firstName, client.lastName)}
            </span>
          </div>
          <h1 className="cdp-header__name">
            {client.lastName} {client.firstName}
          </h1>
          <Chip
            color={client.status === "active" ? "success" : "danger"}
            variant="soft"
            size="sm"
          >
            {client.status === "active" ? "Активен" : "Заблокирован"}
          </Chip>
        </div>
        <div className="cdp-header__details">
          {client.phone && (
            <span className="cdp-header__detail">
              <Icon path={mdiPhone} size={0.65} />
              {client.phone}
            </span>
          )}
          {client.email && (
            <span className="cdp-header__detail">
              <Icon path={mdiEmail} size={0.65} />
              {client.email}
            </span>
          )}
          <span className="cdp-header__detail">
            <Icon path={mdiCarOutline} size={0.65} />
            {client.carsCount} авто
          </span>
          {client.registeredAt && (
            <span className="cdp-header__detail cdp-header__detail--muted">
              <Icon path={mdiCalendarOutline} size={0.55} />
              Регистрация:{" "}
              {new Date(client.registeredAt).toLocaleDateString("ru-RU")}
            </span>
          )}
        </div>
        {client.lastVisitAt && (
          <div className="cdp-header__last-visit">
            Последний визит:{" "}
            {new Date(client.lastVisitAt).toLocaleDateString("ru-RU")}
          </div>
        )}
      </div>

      <div
        className={yearNavClass}
        aria-hidden={phase === "hidden" || phase === "exiting"}
        data-testid="cdp-year-nav"
      >
        <button
          type="button"
          className="cdp-year-nav__arrow"
          onClick={() => setStatsYear(statsYear - 1)}
          aria-label="Предыдущий год"
        >
          ‹
        </button>
        {yearOptions.map((y) => (
          <button
            key={y}
            type="button"
            className={`cdp-year-nav__btn${statsYear === y ? " cdp-year-nav__btn--active" : ""}`}
            onClick={() => y <= currentYear && setStatsYear(y)}
            disabled={y > currentYear}
            aria-disabled={y > currentYear}
          >
            {y}
          </button>
        ))}
        <button
          type="button"
          className="cdp-year-nav__arrow"
          onClick={() => setStatsYear(Math.min(statsYear + 1, currentYear))}
          disabled={statsYear >= currentYear}
          aria-label="Следующий год"
        >
          ›
        </button>
      </div>
    </div>
  );
});

ClientHeader.displayName = "ClientHeader";
export default ClientHeader;
